import React, { useState } from 'react';
import {
  Upload, UploadFileInfo, UploadListItemProps, UploadOnAddEvent, UploadOnBeforeUploadEvent, UploadOnProgressEvent, UploadOnRemoveEvent, UploadOnStatusChangeEvent,
} from '@progress/kendo-react-upload';
import { Fade } from "@progress/kendo-react-animation";
import { Notification, NotificationGroup } from '@progress/kendo-react-notification';
import { cancelIcon, filePdfIcon, pencilIcon, saveIcon } from '@progress/kendo-svg-icons';
import {
  IntlProvider,
  load,
  loadMessages,
  LocalizationProvider,
} from '@progress/kendo-react-intl';
import styles from './_file-upload.module.scss';
import likelySubtags from 'cldr-core/supplemental/likelySubtags.json';
import currencyData from 'cldr-core/supplemental/currencyData.json';
import weekData from 'cldr-core/supplemental/weekData.json';
import numbers from 'cldr-numbers-full/main/es/numbers.json';
import caGregorian from 'cldr-dates-full/main/es/ca-gregorian.json';
import dateFields from 'cldr-dates-full/main/es/dateFields.json';
import timeZoneNames from 'cldr-dates-full/main/es/timeZoneNames.json';
import dzVerbiage from './dropzoneVerbiage.json';
import { Button } from '@progress/kendo-react-buttons';
import { TextBox } from '@progress/kendo-react-inputs';
import { StipDocument, StipDocumentWithObjectUrl } from '../../Models/StipDocument';
import { getDocumentTypeForLanguage, getWordingForLanguage } from '../../Helpers/LanguageDictionary';
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { Stipulation } from '../../Models/Stipulation';
import { isNotNil } from 'ramda';
import { hasValue } from '@amsanalytics/react-shared-library';
import { SvgIcon } from '@progress/kendo-react-common';
import { Loader } from '@progress/kendo-react-indicators';
load(
  likelySubtags,
  currencyData,
  weekData,
  numbers,
  caGregorian,
  dateFields,
  timeZoneNames
);
loadMessages(dzVerbiage, 'es-ES');

/**
 * UploadListItemProps:
  * files: the list of files to be uploaded
  * isUploadingFiles: whether or not the files are being uploaded
  * selectedLanguageId: the selected language id
  * fileStipulationMappings: the list of file-stipulation mappings so we know which stipulation each file is associated with
  * stipulations: the list of stipulations to populate the dropdown menu
  * onDropdownSelect: the function to call when a dropdown is selected
  * onFileSelect: user has clicked on a file to view it in full size
  * onViewPdf: user has clicked on a pdf file to view it
*/
const UploadListItem = (props: UploadListItemProps & { isUploadingFiles: boolean, selectedLanguageId: string, fileStipulationMappings: [fileName: string, stipulationId: number | undefined][], stipulations: Stipulation[], onDropdownSelect: (docUid: string, stipulationId: number) => void, onFileSelect: (objectUrl: string, fileName: string) => void, onViewPdf: (url: string, fileName: string) => void }) => {
  return props.isUploadingFiles === true ? (
    <div>
      {getWordingForLanguage('uploadingDocs', props.selectedLanguageId)}
      <Loader type="pulsing" themeColor='primary' style={{ display: 'flex', justifyContent: 'center' }} />
    </div>
  )
    : (
      <div>
        {props.files.map((file: UploadFileInfo, idx: number) => {
          // get raw file
          const rawFile = (file as any).getRawFile() ? URL.createObjectURL((file as any).getRawFile()) : undefined;
          return (
            <div key={idx} style={idx !== 0 ? { display: 'flex', flexDirection: 'column', flex: 1, borderTop: '2px solid gray', marginTop: '10px', paddingTop: '5px' } : { display: 'flex', flexDirection: 'column', flex: 1, paddingTop: '5px' }}>
              <div style={{ fontWeight: 500 }}>{getWordingForLanguage('cannotDeleteDocs', props.selectedLanguageId)}</div>
              <div style={idx === props.files.length - 1 ? { display: 'flex', flexDirection: 'row', flex: 1, alignItems: 'center', justifyContent: 'space-evenly', gap: '5px', flexWrap: 'wrap', paddingTop: '5px' } : { display: 'flex', flexDirection: 'row', flex: 1, alignItems: 'center', justifyContent: 'space-evenly', marginBottom: '10px', gap: '5px', flexWrap: 'wrap', paddingTop: '5px' }}>
                {file.name}
                {isNotNil(rawFile) && file.extension !== '.pdf' && (
                  <div
                    style={{ display: 'flex', cursor: 'pointer' }}
                    onClick={() => {
                      // open image viewer dialog
                      props.onFileSelect(rawFile!, file.name);
                    }}
                  >
                    <img
                      src={rawFile}
                      alt={file.name}
                      style={{ width: '50px' }}
                    />
                  </div>
                )}
                {file.extension === '.pdf' && (
                  <div style={{ display: 'flex', flex: 1, alignSelf: 'center' }}>
                    <SvgIcon
                      icon={filePdfIcon}
                      themeColor='error'
                      size='xlarge'
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        // open pdf viewer dialog
                        props.onViewPdf(rawFile!, file.name);
                      }}
                    />
                  </div>
                )}
              </div>
              <div
                style={{ display: 'flex', flexDirection: 'column', flex: 1 }}
                onClick={(e) => e.stopPropagation()}>
                <DropDownList
                  style={{ display: 'flex', flex: 1, minWidth: '200px' }}
                  data={props.stipulations?.map((s) => {
                    return { id: s.id, name: getDocumentTypeForLanguage(s.name.trim().toLowerCase(), props.selectedLanguageId) };
                  }).sort((a, b) => a.name.localeCompare(b.name))}
                  textField="name"
                  fillMode={'outline'}
                  label={getWordingForLanguage('documentType', props.selectedLanguageId)}
                  value={{...props.stipulations.find((s) => s.id === props.fileStipulationMappings.find((fsm) => fsm[0] === file.name)?.[1]), name: getDocumentTypeForLanguage(props.stipulations.find((s) => s.id === props.fileStipulationMappings.find((fsm) => fsm[0] === file.name)?.[1])?.name.trim().toLowerCase() ?? '', props.selectedLanguageId)}}
                  onChange={(e: DropDownListChangeEvent) => {
                    props.onDropdownSelect(file.name, e.target.value.id as number);
                  }}
                />
                {!props.stipulations.find((s) => s.id === props.fileStipulationMappings.find((fsm) => fsm[0] === file.name)?.[1]) && (
                  <span style={{ color: 'darkred', textAlign: 'center' }}>{getWordingForLanguage('selectDocumentType', props.selectedLanguageId)}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    )
};

export interface FileUploadProps {
  files: StipDocumentWithObjectUrl[];
  selectedLanguageId: string;
  uploadUrl: string; // URL to upload the files to
  applicationCustomerId: string;
  allowedExtensions?: string[];
  canAddDocs: boolean; // whether the user can add documents
  canEditDocs: boolean; // whether the user can edit documents
  stiplations: Stipulation[];
  onEditStipDocument: (stipDocumentId: string, documentName: string, stipulationId: number) => void;
  onUploadStipulationDocuments: (stipulationDocuments: StipDocument[]) => void;
  onViewImage: (objectUrl: string, documentName: string) => void;
  onViewPdf: (url: string, documentName: string) => void;
}

export const FileUpload = (props: FileUploadProps) => {
  // Keeps track of which stipulation document is being edited
  const [stipDocIdForEditing, setStipDocIdForEditing] = useState<string>();
  // Keeps track of the temporary document name while editing
  const [tempStipDocName, setTempStipDocName] = useState<string>('');
  // Keeps track of the temprorary stipulation id while editing
  const [tempStipId, setTempStipId] = useState<number>();
  // Maps the file name to the stipulation id
  const [fileStipulationMappings, setFileStipulationMappings] = useState<[fileName: string, stipulationId: number | undefined][]>([]);
  // whether or not to show the warning dialog (when user uploads files with extensions that are not allowed)
  const [showInvalidExtensionWarning, setShowInvalidExtensionWarning] = useState<boolean>(false);
  const stipulations = props.stiplations;
  // whether or not we should show the list of files in the Kendo Upload component
  const [showFileList, setShowFileList] = useState<boolean>(true);
  // list of files for the Kendo Upload component
  const [files, setFiles] = useState<UploadFileInfo[]>([]);
  // IDs of the files being uploaded
  const [isUploadingFiles, setIsUploadingFiles] = useState<boolean>(false);
  // local reference to the selected language id
  const selectedLanguageId = props.selectedLanguageId;
  // local reference to the onViewImage function prop
  const onViewImage = props.onViewImage;
  // local reference to the onViewPdf function prop
  const onViewPdf = props.onViewPdf;
  // returns true if we should show the image preview for the file
  const showImagePreview = (file: StipDocumentWithObjectUrl) => {
    return isNotNil(file.objectUrl) && file.mimeType !== 'application/pdf'
  }
  // returns true if we should show the 'open pdf' button for the file
  const showPdfButton = (file: StipDocumentWithObjectUrl) => {
    return (file.mimeType === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) && hasValue(file.objectUrl);
  }

  return (
    <div className={styles.fileUploadContainer}>
      <LocalizationProvider language={props.selectedLanguageId === '1' ? 'en-US' : 'es-ES'}>
        <IntlProvider locale={props.selectedLanguageId === '1' ? 'en-US' : 'es-ES'}>
          <Upload
            files={files}
            batch={true}
            multiple={true}
            autoUpload={false}
            showFileList={showFileList}
            disabled={props.canAddDocs !== true}
            showActionButtons={!isUploadingFiles && !fileStipulationMappings.some((fsm) => fsm[1] === undefined)}
            onAdd={(e: UploadOnAddEvent) => {
              // if file extensions are not allowed, do not add the files
              if (e.affectedFiles.some((file) => !props.allowedExtensions?.includes(file.extension || file.name.toLowerCase().substring(file.name.lastIndexOf('.'))))) {
                setShowInvalidExtensionWarning(true);
                return;
              }
              setFiles(e.affectedFiles);
              setShowFileList(true);
              // set initial document mapping for each file
              setFileStipulationMappings(e.affectedFiles.map((file: UploadFileInfo) => {
                return [file.name, undefined];
              }));
            }}
            onRemove={(e: UploadOnRemoveEvent) => {
              setFiles(e.newState);
            }}

            onProgress={(e: UploadOnProgressEvent) => setFiles(e.affectedFiles)}
            onStatusChange={(e: UploadOnStatusChangeEvent) => {
              if (e.response?.response) {
                props.onUploadStipulationDocuments(e.response?.response as StipDocument[]);
                // clear list of files shown by Kendo Upload because we are handling the list of files ourselves in redux.
                setFiles(e.affectedFiles);
                // hide the list of files shown by Kendo Upload
                setShowFileList(false);
                // turn off loading indicator
                setIsUploadingFiles(false);
              }
            }}
            saveUrl={props.uploadUrl}
            onBeforeUpload={(e: UploadOnBeforeUploadEvent) => {
              setIsUploadingFiles(true);
              // stringify the file stipulation mappings and add property names to the name and stipId
              e.additionalData = {
                'documentMapping': JSON.stringify(fileStipulationMappings.map((fsm) => {
                  return { 'fileName': fsm[0], 'stipulationId': fsm[1] };
                }))
              }
            }}
            restrictions={{
              allowedExtensions: props.allowedExtensions || [
                '.jpg',
                '.jpeg',
                '.pdf',
                '.png',
              ],
            }}
            listItemUI={(props: UploadListItemProps) => (
              <UploadListItem
                {...props}
                isUploadingFiles={isUploadingFiles}
                stipulations={stipulations}
                fileStipulationMappings={fileStipulationMappings}
                selectedLanguageId={selectedLanguageId}
                onDropdownSelect={(docUid: string, stipulationId: number) => {
                  setFileStipulationMappings(fileStipulationMappings.map((fsm) => {
                    if (fsm[0] === docUid) {
                      return [fsm[0], stipulationId];
                    } else {
                      return fsm;
                    }
                  }));
                }}
                onFileSelect={(objectUrl: string, fileName: string) => {
                  onViewImage(objectUrl, fileName);
                }}
                onViewPdf={(url: string, fileName: string) => {
                  onViewPdf(url, fileName);
                }}
              />
            )}
          />
        </IntlProvider>
      </LocalizationProvider>
      {props.files && props.files.length > 0 ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1em',
            padding: '10px'
          }}
        >
          <div style={{ fontWeight: 500 }}>{getWordingForLanguage('submittedDocuments', props.selectedLanguageId)}:</div>
          <div style={{ display: 'flex', flexDirection: 'row', gap: '10px', flexWrap: 'wrap' }}>
            {props.files.sort((a, b) => a.name.localeCompare(b.name)).map((file: StipDocumentWithObjectUrl, index: number) => {
              return (
                <div key={index} style={{ display: 'flex', flexDirection: 'row', flex: 1 }}>
                  {file.id && stipDocIdForEditing === file.id && (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'baseline', gap: '10px' }}>
                        <TextBox
                          value={tempStipDocName}
                          onChange={(e) => setTempStipDocName(e.value as string)}
                          fillMode="outline"
                          placeholder='Name'
                          style={{
                            display: 'flex',
                            flex: 1,
                          }}
                          size="small"
                        />
                        <DropDownList
                          style={{ display: 'flex', flex: 1 }}
                          data={stipulations?.map((s) => {
                            return { id: s.id, name: getDocumentTypeForLanguage(s.name.trim().toLowerCase(), props.selectedLanguageId) };
                          }).sort((a, b) => a.name.localeCompare(b.name))}
                          textField="name"
                          fillMode={'outline'}
                          label={getWordingForLanguage('type', props.selectedLanguageId)}
                          value={{ ...stipulations.find((s) => s.id === tempStipId), name: getDocumentTypeForLanguage(stipulations.find((s) => s.id === tempStipId)?.name.trim().toLowerCase() ?? '', props.selectedLanguageId) }}
                          onChange={(e) => {
                            setTempStipId(e.target.value.id as number);
                          }}
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'row', marginTop: '1em', justifyContent: 'flex-end' }}>
                        <Button
                          onClick={(e) => {
                            file.id && props.onEditStipDocument(file.id, tempStipDocName, tempStipId ?? file.stipulationId);
                            setTempStipDocName('');
                            setTempStipId(undefined);
                            setStipDocIdForEditing(undefined);
                          }}
                          fillMode="outline"
                          svgIcon={saveIcon}
                          themeColor={'success'}
                          disabled={stipDocIdForEditing !== file.id || tempStipDocName.trim().length < 1}
                        >
                          {getWordingForLanguage('save', props.selectedLanguageId)}
                        </Button>
                        <Button
                          style={{ marginLeft: '5px' }}
                          themeColor="error"
                          svgIcon={cancelIcon}
                          fillMode={'outline'}
                          onClick={() => {
                            setTempStipDocName('');
                            setTempStipId(undefined);
                            setStipDocIdForEditing(undefined);
                          }}>
                          {getWordingForLanguage('cancel', props.selectedLanguageId)}
                        </Button>
                      </div>
                    </div>
                  )}
                  {file.id && stipDocIdForEditing !== file.id && (
                    <div style={{ display: 'flex', flexDirection: 'column', flexWrap: 'wrap', gap: '3px' }}>
                      <div style={showPdfButton(file) === true ? { display: 'flex', alignItems: 'flex-start' } : { display: 'flex', alignItems: 'flex-start', flex: 1 }}>
                        {
                          props.canEditDocs && (
                            <Button
                              fillMode="flat"
                              svgIcon={pencilIcon}
                              themeColor={'primary'}
                              style={{ marginRight: '3px', padding: 0 }}
                              onClick={(e) => {
                                setTempStipDocName(file.name);
                                setTempStipId(file.stipulationId);
                                setStipDocIdForEditing(file.id);
                              }}
                            />
                          )
                        }
                        {' '}
                        <div style={{ display: 'flex', flexDirection: 'column', }}>
                          <span style={{ fontWeight: 500, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', maxWidth: '250px' }}>{props.files.find((f) => f.id === file.id)?.name}</span>
                          <small>{getWordingForLanguage('types', props.selectedLanguageId)}:&nbsp;{stipulations.filter((s) => s.id === file.stipulationId).map((s) => getDocumentTypeForLanguage(s.name.trim().toLowerCase(), props.selectedLanguageId)).join(', ')}</small>
                        </div>
                      </div>
                      {showImagePreview(file) === true && (
                        <div
                          style={{ display: 'flex', flex: 1, cursor: 'pointer', marginLeft: '5px' }}
                          onClick={() => {
                            // open image viewer dialog
                            props.onViewImage(file.objectUrl!, file.name);
                          }}
                        >
                          <img
                            src={file.objectUrl}
                            alt={file.name}
                            style={{ width: '50px' }}
                          />
                        </div>
                      )}
                      {showPdfButton(file) === true && (
                        <div style={{ display: 'flex', margin: '0 auto', width: 'fit-content' }}>
                          <SvgIcon
                            icon={filePdfIcon}
                            themeColor='error'
                            size='xlarge'
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              // open pdf viewer dialog
                              props.onViewPdf(file.objectUrl!, file.name);
                            }}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : undefined}
      <NotificationGroup
        style={{
          right: 0,
          bottom: 0,
          alignItems: "flex-start",
          flexWrap: "wrap-reverse",
        }}
      >
        <Fade>
          {showInvalidExtensionWarning && (
            <Notification
              type={{ style: "warning", icon: true }}
              closable={true}
              onClose={() => setShowInvalidExtensionWarning(false)}
            >
              <span>{getWordingForLanguage('invalidExtensions', props.selectedLanguageId)}</span>
            </Notification>
          )}
        </Fade>
      </NotificationGroup>
    </div>
  );
};
